import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, XCircle, MinusCircle, RefreshCw } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Skeleton } from '../components/ui/Skeleton.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

type AlarmState = 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';

interface Alarm {
  name: string;
  state: AlarmState;
  description?: string;
}

interface ServiceStatus {
  ok: boolean;
  [key: string]: number | boolean | null;
}

interface StatusResponse {
  alarms: Alarm[] | null;
  services: {
    lambda: { ok: boolean; errors5min: number | null; durationP99ms: number | null; invocationsToday: number | null };
    rds: { ok: boolean; cpuPct: number | null; connections: number | null };
    ses: { ok: boolean; sent24h: number | null; bounceRate: number | null };
    sqs: Record<string, { depth: number | null }>;
    stepFunctions: { ok: boolean; running: number | null; failed5min: number | null };
  };
  metrics: {
    emailsSent24h: number | null;
    activeAutomations: number | null;
    newCustomers24h: number | null;
    todayCostUsd: number | null;
  };
  partial: boolean;
  fetchedAt: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function AlarmBadge({ state }: { state: AlarmState }) {
  if (state === 'ALARM') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
        <XCircle size={9} /> ALARM
      </span>
    );
  }
  if (state === 'OK') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700">
        <CheckCircle size={9} /> OK
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-500">
      <MinusCircle size={9} /> N/A
    </span>
  );
}

function ServiceBadge({ ok, unavailable }: { ok: boolean; unavailable?: boolean }) {
  if (unavailable) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-400">
        N/A
      </span>
    );
  }
  if (ok) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700">
        <CheckCircle size={9} /> OK
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
      <XCircle size={9} /> WARN
    </span>
  );
}

function MetricRow({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="flex justify-between text-[10px]">
      <span className="text-zinc-400">{label}</span>
      <span className="font-semibold text-zinc-700">{value !== null ? value : '—'}</span>
    </div>
  );
}

function formatUsd(n: number | null): string {
  if (n === null) return '—';
  return `$${n.toFixed(2)}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function PlatformStatusPage() {
  const { data, isLoading, isError, error, dataUpdatedAt } = useQuery<StatusResponse>({
    queryKey: ['platform-status'],
    queryFn: () => api.get('/sa/platform/status').then((r) => r.data),
    refetchInterval: 60_000,
  });

  const secondsAgo = dataUpdatedAt
    ? Math.floor((Date.now() - dataUpdatedAt) / 1000)
    : null;

  const alarmCount = data?.alarms?.filter((a) => a.state === 'ALARM').length ?? 0;

  if (isError) {
    return (
      <div>
        <PageHeader title="Platform Status" />
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {extractError(error)}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <PageHeader title="Platform Status" />
        <div className="flex items-center gap-3">
          {secondsAgo !== null && (
            <span className="text-xs text-zinc-400 flex items-center gap-1">
              <RefreshCw size={10} className="text-zinc-300" />
              Updated {secondsAgo}s ago · auto-refresh 60s
            </span>
          )}
          {data?.partial && (
            <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Partial data
            </span>
          )}
        </div>
      </div>

      {/* ── Active alarm banner ────────────────────────────────────────────── */}
      {!isLoading && alarmCount > 0 && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2.5">
          <AlertTriangle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              {alarmCount} active alarm{alarmCount > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-red-600 mt-0.5">
              {data?.alarms
                ?.filter((a) => a.state === 'ALARM')
                .map((a) => a.name)
                .join(' · ')}
            </p>
          </div>
        </div>
      )}

      {/* ── CloudWatch Alarms ─────────────────────────────────────────────── */}
      <div className="mb-4">
        <h3 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">
          CloudWatch Alarms
        </h3>
        {isLoading ? (
          <div className="space-y-1.5">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : !data?.alarms ? (
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-400">
            Alarm data unavailable
          </div>
        ) : (
          <div className="space-y-1.5">
            {data.alarms.map((alarm) => (
              <div
                key={alarm.name}
                className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
                  alarm.state === 'ALARM'
                    ? 'border-red-200 bg-red-50'
                    : 'border-zinc-200 bg-white'
                }`}
              >
                <AlarmBadge state={alarm.state} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-zinc-800 truncate">{alarm.name}</p>
                  {alarm.description && (
                    <p className="text-[10px] text-zinc-400 truncate">{alarm.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Service Health ─────────────────────────────────────────────────── */}
      <div className="mb-4">
        <h3 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">
          Service Health
        </h3>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {/* Lambda */}
            <ServiceCard
              title="Lambda API"
              ok={data?.services.lambda.ok ?? true}
              unavailable={!data}
            >
              <MetricRow label="Errors (5min)" value={data?.services.lambda.errors5min ?? '—'} />
              <MetricRow
                label="Latency p99"
                value={
                  data?.services.lambda.durationP99ms !== null && data?.services.lambda.durationP99ms !== undefined
                    ? `${Math.round(data.services.lambda.durationP99ms)}ms`
                    : '—'
                }
              />
              <MetricRow
                label="Invocations"
                value={data?.services.lambda.invocationsToday ?? '—'}
              />
            </ServiceCard>

            {/* RDS */}
            <ServiceCard
              title="RDS Aurora"
              ok={data?.services.rds.ok ?? true}
              unavailable={!data}
            >
              <MetricRow
                label="CPU"
                value={
                  data?.services.rds.cpuPct !== null && data?.services.rds.cpuPct !== undefined
                    ? `${data.services.rds.cpuPct.toFixed(1)}%`
                    : '—'
                }
              />
              <MetricRow
                label="Connections"
                value={data?.services.rds.connections !== null ? Math.round(data?.services.rds.connections ?? 0) : '—'}
              />
            </ServiceCard>

            {/* SES */}
            <ServiceCard
              title="SES"
              ok={data?.services.ses.ok ?? true}
              unavailable={!data}
            >
              <MetricRow label="Sent 24h" value={data?.services.ses.sent24h?.toLocaleString() ?? '—'} />
              <MetricRow
                label="Bounce rate"
                value={
                  data?.services.ses.bounceRate !== null && data?.services.ses.bounceRate !== undefined
                    ? `${data.services.ses.bounceRate}%`
                    : '—'
                }
              />
            </ServiceCard>

            {/* SQS */}
            <ServiceCard
              title="SQS Queues"
              ok={
                Object.values(data?.services.sqs ?? {}).every(
                  (q) => (q.depth ?? 0) === 0,
                ) ?? true
              }
              unavailable={!data}
            >
              {Object.entries(data?.services.sqs ?? {}).map(([name, q]) => (
                <MetricRow
                  key={name}
                  label={name}
                  value={q.depth}
                />
              ))}
            </ServiceCard>

            {/* Step Functions */}
            <ServiceCard
              title="Step Functions"
              ok={data?.services.stepFunctions.ok ?? true}
              unavailable={!data}
            >
              <MetricRow label="Executions (5min)" value={data?.services.stepFunctions.running ?? '—'} />
              <MetricRow label="Failed (5min)" value={data?.services.stepFunctions.failed5min ?? '—'} />
            </ServiceCard>
          </div>
        )}
      </div>

      {/* ── Business Metrics ──────────────────────────────────────────────── */}
      <div>
        <h3 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-2">
          Business Metrics — Last 24h
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Emails Sent', value: data?.metrics.emailsSent24h?.toLocaleString() ?? null },
            { label: 'Active Automations', value: data?.metrics.activeAutomations?.toString() ?? null },
            { label: 'New Customers', value: data?.metrics.newCustomers24h?.toLocaleString() ?? null },
            { label: "Today's Spend", value: formatUsd(data?.metrics.todayCostUsd ?? null) },
          ].map((card) => (
            <div key={card.label} className="bg-white border border-zinc-200 rounded-lg p-3 text-center">
              {isLoading ? (
                <Skeleton className="h-7 w-16 mx-auto mb-1" />
              ) : (
                <p className="text-xl font-bold text-violet-600">{card.value ?? '—'}</p>
              )}
              <p className="text-[10px] text-zinc-400 mt-1">{card.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ServiceCard helper component ─────────────────────────────────────────────

function ServiceCard({
  title,
  ok,
  unavailable,
  children,
}: {
  title: string;
  ok: boolean;
  unavailable?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-zinc-800">{title}</p>
        <ServiceBadge ok={ok} unavailable={unavailable} />
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
